import styled, { css, keyframes } from "styled-components";
import { Bell } from "lucide-react";
import { MOBILE_MAX } from "../../styles/authShellMixins";

export const SidebarContainer = styled.div`
  background: var(--sidebar-bg);
  width: ${({ collapsed }) => (collapsed ? "6vw" : "15vw")};
  min-width: ${({ collapsed }) => (collapsed ? "64px" : "180px")};
  height: 100vh;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
  z-index: 800;
  box-shadow: var(--shadow-md);
  transition: width 0.3s ease-in-out, transform 0.3s ease-in-out,
    opacity 0.3s ease-in-out;

  & + main {
    margin-left: ${({ collapsed }) => (collapsed ? "3vw" : "10vw")};
    transition: margin-left 0.3s ease-in-out;
  }

  @media screen and (max-width: ${MOBILE_MAX}) {
    position: fixed;
    top: 0;
    left: 0;
    width: min(82vw, 300px);
    min-width: 0;
    height: 100dvh;
    transform: ${({ $open }) => ($open ? "translateX(0)" : "translateX(-105%)")};
    opacity: 1;
    pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
    box-shadow: ${({ $open }) => ($open ? "var(--shadow-lg)" : "none")};
  }
`;

export const MobileMenuButton = styled.button`
  display: none;

  @media screen and (max-width: ${MOBILE_MAX}) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0.65rem;
    left: 0.65rem;
    z-index: 900;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 12px;
    border: 1px solid var(--border-default);
    background: var(--surface-1);
    color: var(--text-heading);
    box-shadow: var(--shadow-md);
    cursor: pointer;
    font-size: 1.35rem;
    line-height: 1;
    padding: 0;

    &:hover {
      background: var(--surface-2);
    }
  }
`;

export const MobileSidebarBackdrop = styled.button`
  display: none;

  @media screen and (max-width: ${MOBILE_MAX}) {
    display: ${({ $open }) => ($open ? "block" : "none")};
    position: fixed;
    inset: 0;
    z-index: 750;
    border: none;
    margin: 0;
    padding: 0;
    background: color-mix(in srgb, var(--brand-navy, #00212d) 55%, transparent);
    cursor: pointer;
  }
`;

export const SidebarHeader = styled.div`
  background-color: transparent;
  height: 8vh;
  min-height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-bottom: 2px solid var(--border-default);
  flex-shrink: 0;

  @media screen and (max-width: ${MOBILE_MAX}) {
    display: flex;
    min-height: 56px;
    height: auto;
    padding: 0.75rem;
  }
`;

export const SidebarMenu = styled.ul`
  height: 100%;
  list-style: none;
  margin: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 5px 0;
  gap: 1vh;
  overflow: hidden;

  @media screen and (max-width: ${MOBILE_MAX}) {
    justify-content: flex-start;
    padding-top: 1rem;
    gap: 0.35rem;
    /* No mobile o drawer precisa rolar se houver muitos itens */
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const settingsHintGlow = keyframes`
  0%, 100% {
    box-shadow:
      0 0 0 1px color-mix(in srgb, var(--accent-secondary) 40%, transparent),
      0 0 12px var(--accent-soft);
  }
  50% {
    box-shadow:
      0 0 0 2px var(--border-strong),
      0 0 20px color-mix(in srgb, var(--accent-secondary) 50%, transparent);
  }
`;

const settingsHintNudge = keyframes`
  0%, 82%, 100% { transform: translateX(0); }
  86% { transform: translateX(-3px); }
  90% { transform: translateX(3px); }
  94% { transform: translateX(-2px); }
  98% { transform: translateX(1px); }
`;

const tipBounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-2px); }
`;

export const SidebarMenuItem = styled.li`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ collapsed }) => (collapsed ? "0.6vh" : "0.8vh 2vh")};
  font-size: ${({ collapsed }) => (collapsed ? "0.7rem" : "0.8rem")};
  font-weight: 500;
  color: var(--text-primary);
  cursor: pointer;
  text-align: center;
  transition: all 0.3s ease;
  border-radius: 8px;
  margin: 8px auto;
  width: ${({ collapsed }) => (collapsed ? "auto" : "calc(100% - 16px)")};
  min-height: 4vh;

  &::after {
    width: 80px;
    height: auto;
    content: attr(data-label);
    position: absolute;
    background: var(--surface-overlay);
    color: var(--text-heading);
    padding: 8px;
    border-radius: 10px;
    top: ${({ collapsed }) => (collapsed ? "0px" : "50%")};
    left: ${({ collapsed }) => (collapsed ? "50%" : "120%")};
    transform: ${({ collapsed }) =>
      collapsed ? "translateX(-50%)" : "translateY(-50%)"};
    z-index: 1000;
    opacity: 0;
    transition: opacity 0.3s ease-in-out, top 0.3s ease-in-out;
    visibility: ${({ collapsed }) => (collapsed ? "visible" : "hidden")};
    box-shadow: var(--shadow-md);
    border: none;
    white-space: nowrap;
    pointer-events: none;
    text-align: center;
  }

  &:hover {
    background: var(--accent-soft);
    color: var(--text-primary);
    &::after {
      opacity: 1;
    }
  }

  &.active {
    background: linear-gradient(
      90deg,
      var(--accent) 0%,
      var(--accent-secondary) 100%
    );
    color: var(--text-on-accent);
    font-weight: 600;
  }

  ${({ $featureHint }) =>
    $featureHint &&
    css`
      animation:
        ${settingsHintGlow} 2.2s ease-in-out infinite,
        ${settingsHintNudge} 4.5s ease-in-out infinite;
      border: 1px solid var(--border-strong);
      background: var(--accent-soft);

      &.active {
        animation: none;
      }
    `}

  @media screen and (max-width: ${MOBILE_MAX}) {
    font-size: 0.95rem;
    min-height: 2.75rem;
    width: calc(100% - 1rem);
    padding: 0.65rem 0.75rem;
    margin: 0.2rem auto;

    &::after {
      display: none;
    }
  }
`;

export const SettingsHintBadge = styled.span`
  position: absolute;
  top: -0.35rem;
  right: ${({ $collapsed }) => ($collapsed ? "-0.2rem" : "0.15rem")};
  z-index: 2;
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.35rem;
  border-radius: 999px;
  font-size: 0.55rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-on-accent);
  background: linear-gradient(90deg, var(--accent), var(--accent-secondary));
  box-shadow: 0 0 10px var(--accent-soft);
  animation: ${tipBounce} 1.6s ease-in-out infinite;
  pointer-events: none;
`;

export const SidebarMenuItemDiffer = styled.li`
  display: flex;
  align-items: center;
  justify-content: center;
  color: transparent;
  cursor: pointer;
  text-align: center;
  transition: all 0.3s ease;
  border-radius: 2vw;
  background: transparent;
  height: 40px;
  width: 40px;
  padding: 0.5vw;
  margin: 0 auto 10px auto;

  &:hover {
    background: var(--accent-soft);
    color: transparent;
    border-radius: 2vw;
  }

  &.active {
    background: var(--accent-soft);
    color: transparent;
    border: 1px solid var(--border-default);
    box-shadow: var(--shadow-sm);
  }

  @media screen and (max-width: ${MOBILE_MAX}) {
    display: none;
  }
`;

export const SidebarFooter = styled.div`
  background-color: transparent;
  height: 8vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-top: 2px solid var(--border-default);
  flex-shrink: 0;
  padding: 10px 0;

  @media screen and (max-width: ${MOBILE_MAX}) {
    display: flex;
    height: auto;
    min-height: 48px;
    padding: 0.75rem;
  }
`;

export const SidebarIcon = styled.img`
  width: 2vw;
  min-width: 22px;
  max-width: 28px;
  height: auto;

  @media screen and (max-width: ${MOBILE_MAX}) {
    width: 22px;
  }
`;

/** Ícone Lucide no menu colapsado — mesmo porte e cor dos PNGs (SidebarIcon). */
export const SidebarLucideIcon = styled(Bell)`
  width: 2vw;
  min-width: 24px;
  max-width: 28px;
  height: 2vw;
  min-height: 24px;
  max-height: 28px;
  flex-shrink: 0;
  color: var(--accent);
  stroke: currentColor;
  fill: none;
  opacity: 1;

  @media screen and (max-width: ${MOBILE_MAX}) {
    width: 24px;
    height: 24px;
    min-width: 24px;
    min-height: 24px;
  }
`;
